import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef
} from 'react'
import { useAtomValue } from 'jotai'

import TwitterAuth from '@/components/TwitterAuth'
import { LoginStatus } from '@/constants/common'
import { TwitterAuthState } from '@/hooks/services/useTwitterAuth'
import { useUserInfo } from '@/hooks/services/useUserInfo'
import useSignAndLogin from '@/hooks/useSignAndLogin'
import { loginStatusAtom, twitterAuthStateAtom } from '@/stores/services'

export type GlobalMethodProviderProps = {
  loginLoading: boolean
  signMessageWithLogin: () => Promise<
    { signature: string; signatureMessage: string } | undefined
  >
  refreshUserInfo: () => void
  openTwitterAuth: () => void
  closeTwitterAuth: () => void
}

export const GlobalMethodProviderContext =
  createContext<GlobalMethodProviderProps>(
    undefined! as GlobalMethodProviderProps
  )

export const useGlobalMethodProviderContext = (): GlobalMethodProviderProps =>
  useContext<GlobalMethodProviderProps>(GlobalMethodProviderContext)

const GlobalMethodProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { loginLoading, signMessageWithLogin } = useSignAndLogin()
  const { mutate } = useUserInfo()
  const loginStatus = useAtomValue(loginStatusAtom)
  const twitterAuthState = useAtomValue(twitterAuthStateAtom)

  const twitterAuthRef = useRef<React.ComponentRef<typeof TwitterAuth>>(null)

  const openTwitterAuth = useCallback(() => {
    if (loginStatus !== LoginStatus.LOGIN) return
    if (twitterAuthState === TwitterAuthState.UN_KNOWN) return

    twitterAuthRef.current?.open()
  }, [loginStatus, twitterAuthState])

  return (
    <GlobalMethodProviderContext.Provider
      value={{
        loginLoading,
        signMessageWithLogin,
        refreshUserInfo: () => {
          mutate()
        },
        openTwitterAuth,
        closeTwitterAuth: () => {
          twitterAuthRef.current?.close()
        }
      }}
    >
      <TwitterAuth ref={twitterAuthRef} />
      {children}
    </GlobalMethodProviderContext.Provider>
  )
}

export default GlobalMethodProvider
